import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Badge } from './ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { toast } from 'sonner';
import { 
  Folder,
  File,
  FileText,
  FileImage,
  FileVideo,
  FileAudio,
  FileSpreadsheet,
  FileCode,
  Download,
  Upload,
  Trash2,
  Loader2,
  ChevronLeft,
  Eye,
  ExternalLink,
  FolderOpen,
  AlertCircle,
  X,
  RefreshCw,
  List,
  LayoutGrid
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { uploadInChunks, isImageName, SIMPLE_UPLOAD_BYTES, MAX_UPLOAD_BYTES } from './fileBrowserUpload';
import { FileLightbox } from './FileLightbox';
import { FileShareControl, ShareBadge } from './FileShareControl';

// File type icons based on extension
const getFileIcon = (fileName) => {
  const ext = fileName.split('.').pop()?.toLowerCase();
  
  const imageExts = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp'];
  const videoExts = ['mp4', 'mov', 'avi', 'mkv', 'webm'];
  const audioExts = ['mp3', 'wav', 'ogg', 'flac', 'aac'];
  const spreadsheetExts = ['xlsx', 'xls', 'csv'];
  const codeExts = ['js', 'ts', 'jsx', 'tsx', 'py', 'html', 'css', 'json'];
  const docExts = ['pdf', 'doc', 'docx', 'txt', 'rtf'];
  
  if (imageExts.includes(ext)) return <FileImage className="w-5 h-5 text-green-400" />;
  if (videoExts.includes(ext)) return <FileVideo className="w-5 h-5 text-purple-400" />;
  if (audioExts.includes(ext)) return <FileAudio className="w-5 h-5 text-pink-400" />;
  if (spreadsheetExts.includes(ext)) return <FileSpreadsheet className="w-5 h-5 text-emerald-400" />;
  if (codeExts.includes(ext)) return <FileCode className="w-5 h-5 text-amber-400" />;
  if (docExts.includes(ext)) return <FileText className="w-5 h-5 text-blue-400" />;
  
  return <File className="w-5 h-5 text-gray-400" />;
};

// Format file size
const formatFileSize = (bytes) => {
  if (!bytes) return '-';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

// Check if file is previewable
const isPreviewable = (fileName) => {
  const ext = fileName.split('.').pop()?.toLowerCase();
  const previewableExts = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp', 'heic', 'heif', 'pdf'];
  return previewableExts.includes(ext);
};

export function FileBrowser({ 
  entityType, // 'project' or 'tender'
  entityId,
  entityRef, // e.g., "LUX100 - 123 Main St"
  canUpload = true,
  canDelete = false
}) {
  const { api, isAdmin } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [subfolders, setSubfolders] = useState([]);
  // Legacy/current folder pairs both present on SharePoint (earlier filing bug) → offer a merge.
  const [duplicates, setDuplicates] = useState([]);
  const [merging, setMerging] = useState(false);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [files, setFiles] = useState([]);
  const [filesLoading, setFilesLoading] = useState(false);
  const [viewMode, setViewMode] = useState('list'); // 'list' | 'grid'
  const [error, setError] = useState(null);
  
  // Upload state
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadFolder, setUploadFolder] = useState('');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(null); // { done, total, current }
  
  // Lightbox state — index into the folder's previewable files
  const [lightboxIndex, setLightboxIndex] = useState(null);
  // Admin-only: which field roles each project file is shared with (file_id -> roles[])
  const [shares, setShares] = useState({});
  const canShare = entityType === 'project' && isAdmin;

  const fetchShares = useCallback(async () => {
    if (!canShare) return;
    try {
      const r = await api.get(`/projects/${entityId}/file-shares`);
      setShares(r.data.shares || {});
    } catch { /* non-fatal */ }
  }, [api, entityId, canShare]);

  useEffect(() => { fetchShares(); }, [fetchShares]);

  const onShareSaved = (ids, roles) => setShares(prev => {
    const next = { ...prev };
    ids.forEach(id => { next[id] = roles; });
    return next;
  });
  const previewFiles = files.filter(f => f.type === 'file' && isPreviewable(f.name));

  // Fetch subfolders on mount
  useEffect(() => {
    fetchSubfolders();
  }, [entityId, entityType]);

  const fetchSubfolders = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get(`/${entityType}s/${entityId}/files/subfolders`);
      setSubfolders(response.data.subfolders || []);
      setDuplicates(response.data.duplicates || []);
    } catch (err) {
      console.error('Failed to fetch subfolders:', err);
      setError(err.response?.data?.detail || 'Failed to load folders. Please ensure OneDrive is connected in Settings.');
    } finally {
      setLoading(false);
    }
  };

  const fetchFiles = async (subfolder) => {
    setFilesLoading(true);
    setError(null);
    try {
      const response = await api.get(`/${entityType}s/${entityId}/files`, {
        params: { subfolder }
      });
      const items = response.data.items || [];
      setFiles(items);
      // Photo folders open as a gallery; everything else as a list
      const docs = items.filter(f => f.type === 'file');
      setViewMode(docs.length && docs.filter(f => isImageName(f.name)).length / docs.length >= 0.5 ? 'grid' : 'list');
    } catch (err) {
      console.error('Failed to fetch files:', err);
      const errorMsg = err.response?.data?.detail || 'Failed to load files';
      if (errorMsg.includes('401') || errorMsg.includes('Unauthorized') || errorMsg.includes('OneDrive')) {
        setError('SharePoint session expired. Please reconnect OneDrive in Settings.');
      } else {
        setError(errorMsg);
      }
      setFiles([]);
    } finally {
      setFilesLoading(false);
    }
  };

  const handleFolderClick = (folder) => {
    setSelectedFolder(folder);
    fetchFiles(folder);
  };

  // Nested folders (e.g. 10_Site_Photos/27-09-2026) — the path is `/`-joined.
  const openSubfolder = (name) => handleFolderClick(`${selectedFolder}/${name}`);
  const pathParts = selectedFolder ? selectedFolder.split('/') : [];
  const folderItems = files.filter(f => f.type === 'folder');

  const handleBackToFolders = () => {
    const up = pathParts.slice(0, -1);
    if (up.length) { handleFolderClick(up.join('/')); return; }
    setSelectedFolder(null);
    setFiles([]);
    setError(null); // Clear any file loading errors when going back
  };

  /** Fold the stray legacy folders (09. Building Control / 11. Site Photos) into the real ones. */
  const handleMergeLegacy = async () => {
    setMerging(true);
    try {
      const r = await api.post(`/${entityType}s/${entityId}/files/merge-legacy-folders?dry_run=false`);
      const { moved_files: mf = 0, moved_folders: mo = 0, merged = [] } = r.data || {};
      toast.success(`Tidied ${merged.map(m => m.legacy).join(' and ')} — ${mf} file${mf === 1 ? '' : 's'} and ${mo} folder${mo === 1 ? '' : 's'} moved into place`);
      setDuplicates([]);
      setSelectedFolder(null);
      setFiles([]);
      fetchSubfolders();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not merge the folders');
    } finally {
      setMerging(false);
    }
  };

  // Drag and drop handlers
  const [isDragging, setIsDragging] = useState(false);
  const [dragTargetFolder, setDragTargetFolder] = useState(null);

  const handleDragOver = (e, folder = null) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
    if (folder) {
      setDragTargetFolder(folder);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    setDragTargetFolder(null);
  };

  /** Upload several files one after another; anything over 4MB goes to SharePoint in chunks. */
  const uploadFiles = async (fileList, targetFolder) => {
    const queue = Array.from(fileList || []);
    if (!queue.length || !targetFolder) return { ok: 0 };
    setUploading(true);
    let ok = 0;
    const failed = [];
    let sessionExpired = false;
    for (let i = 0; i < queue.length; i++) {
      const file = queue[i];
      setUploadProgress({ done: i, total: queue.length, current: file.name, fraction: 0 });
      try {
        if (file.size > MAX_UPLOAD_BYTES) { failed.push(`${file.name} (over 250MB)`); continue; }
        if (file.size > SIMPLE_UPLOAD_BYTES) {
          await uploadInChunks(api, entityType, entityId, file, targetFolder,
            (fraction) => setUploadProgress({ done: i, total: queue.length, current: file.name, fraction }));
        } else {
          const formData = new FormData();
          formData.append('file', file);
          formData.append('subfolder', targetFolder);
          await api.post(`/${entityType}s/${entityId}/files/upload`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
        }
        ok++;
      } catch (err) {
        console.error('Failed to upload file:', err);
        const errorMsg = err.response?.data?.detail || err.message || 'Failed to upload file';
        if (errorMsg.includes('401') || errorMsg.includes('Unauthorized') || errorMsg.includes('OneDrive')) { sessionExpired = true; break; }
        failed.push(`${file.name} (${errorMsg})`);
      }
    }
    setUploadProgress(null);
    setUploading(false);
    if (ok) toast.success(ok === 1 ? `Uploaded "${queue[0].name}" to ${targetFolder}` : `Uploaded ${ok} files to ${targetFolder}`);
    if (sessionExpired) toast.error('SharePoint session expired. Please reconnect OneDrive in Settings.');
    else if (failed.length) toast.error(`${failed.length} ${failed.length === 1 ? 'file' : 'files'} not uploaded: ${failed.join(', ')}`);
    if (ok && selectedFolder === targetFolder) fetchFiles(targetFolder);
    return { ok, failed: failed.length };
  };

  const handleDrop = async (e, folder = null) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    setDragTargetFolder(null);

    const droppedFiles = e.dataTransfer?.files;
    if (!droppedFiles || droppedFiles.length === 0) return;

    const targetFolder = folder || selectedFolder;
    if (!targetFolder) {
      toast.error('Please drop files into a specific folder');
      return;
    }
    await uploadFiles(droppedFiles, targetFolder);
  };

  const handleDownload = async (file) => {
    try {
      const response = await api.get(`/${entityType}s/${entityId}/files/download/${file.id}`);
      const downloadUrl = response.data.download_url;
      
      if (downloadUrl) {
        // Open in new tab (triggers download)
        window.open(downloadUrl, '_blank');
      } else {
        toast.error('Download URL not available');
      }
    } catch (err) {
      console.error('Failed to get download URL:', err);
      toast.error('Failed to download file');
    }
  };

  const handlePreview = (file) => {
    const i = previewFiles.findIndex(f => f.id === file.id);
    if (i === -1) { handleDownload(file); return; }
    setLightboxIndex(i);
  };

  const handleOpenInSharePoint = (file) => {
    if (file.web_url) {
      window.open(file.web_url, '_blank');
    }
  };

  const handleUpload = async () => {
    if (!selectedFiles.length || !uploadFolder) {
      toast.error('Please select a folder and at least one file');
      return;
    }
    const { ok, failed } = await uploadFiles(selectedFiles, uploadFolder);
    if (ok && !failed) {
      setUploadDialogOpen(false);
      setSelectedFiles([]);
      setUploadFolder('');
    } else if (ok) {
      // Keep only the ones that didn't make it so they can be retried
      setSelectedFiles([]);
    }
  };

  const addSelectedFiles = (fileList) => {
    const incoming = Array.from(fileList || []);
    setSelectedFiles(prev => {
      const seen = new Set(prev.map(f => `${f.name}-${f.size}`));
      return [...prev, ...incoming.filter(f => !seen.has(`${f.name}-${f.size}`))];
    });
  };

  const handleDelete = async (file) => {
    if (!confirm(`Delete "${file.name}"? This cannot be undone.`)) return;
    
    try {
      await api.delete(`/${entityType}s/${entityId}/files/${file.id}`);
      toast.success('File deleted');
      fetchFiles(selectedFolder);
    } catch (err) {
      console.error('Failed to delete file:', err);
      toast.error(err.response?.data?.detail || 'Failed to delete file');
    }
  };

  if (loading) {
    return (
      <Card className="bg-[#2A3240] border-white/10">
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-[#F5A623]" />
        </CardContent>
      </Card>
    );
  }

  if (error && !selectedFolder) {
    return (
      <Card className="bg-[#2A3240] border-white/10">
        <CardContent className="py-8">
          <div className="text-center">
            <AlertCircle className="w-12 h-12 mx-auto mb-4 text-amber-400" />
            <p className="text-gray-400 mb-4">{error}</p>
            <Button onClick={fetchSubfolders} variant="outline" className="border-white/20 text-gray-300">
              <RefreshCw className="w-4 h-4 mr-2" />
              Retry
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-[#2A3240] border-white/10">
      <CardHeader>
        <div className="flex items-start sm:items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-3 min-w-0">
            <div className="p-2 bg-blue-500/10 rounded-md flex-shrink-0">
              <FolderOpen className="w-5 h-5 text-blue-400" />
            </div>
            <div className="min-w-0">
              <CardTitle className="text-xl text-white">Documents</CardTitle>
              <CardDescription className="text-gray-400 truncate" data-testid="file-browser-breadcrumb">
                {selectedFolder ? (
                  pathParts.map((part, i) => (
                    <span key={`${part}-${i}`}>
                      {i > 0 && <span className="mx-1 text-gray-600">/</span>}
                      {i < pathParts.length - 1 ? (
                        <button type="button" onClick={() => handleFolderClick(pathParts.slice(0, i + 1).join('/'))} className="hover:text-white underline-offset-2 hover:underline" data-testid={`file-breadcrumb-${i}`}>{part}</button>
                      ) : (
                        <span className="text-gray-300">{part}</span>
                      )}
                    </span>
                  ))
                ) : 'SharePoint files and folders'}
              </CardDescription>
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            {selectedFolder && files.some(f => f.type === 'file') && (
              <div className="inline-flex rounded-md border border-white/20 overflow-hidden" data-testid="file-view-toggle">
                <button type="button" onClick={() => setViewMode('list')} title="List" className={`px-2.5 py-2 ${viewMode === 'list' ? 'bg-[#F5A623] text-[#1E2530]' : 'text-gray-300 hover:bg-white/5'}`} data-testid="file-view-list-btn"><List className="w-4 h-4" /></button>
                <button type="button" onClick={() => setViewMode('grid')} title="Gallery" className={`px-2.5 py-2 ${viewMode === 'grid' ? 'bg-[#F5A623] text-[#1E2530]' : 'text-gray-300 hover:bg-white/5'}`} data-testid="file-view-grid-btn"><LayoutGrid className="w-4 h-4" /></button>
              </div>
            )}
            {selectedFolder && (
              <Button
                onClick={handleBackToFolders}
                variant="outline"
                className="border-white/20 text-gray-300 hover:bg-white/5"
                data-testid="file-browser-back-btn"
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Back
              </Button>
            )}
            {canUpload && (
              <Button
                onClick={() => {
                  setUploadFolder(selectedFolder || '');
                  setUploadDialogOpen(true);
                }}
                className="bg-[#F5A623] text-[#1E2530] hover:bg-[#D98E1D]"
                data-testid="file-browser-upload-btn"
              >
                <Upload className="w-4 h-4 mr-2" />
                Upload
              </Button>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {/* Two folder structures on one job (legacy names left by an earlier
            filing bug) — photos are split between them. Admins merge in one tap. */}
        {duplicates.length > 0 && isAdmin && (
          <div className="mb-4 rounded-lg border border-amber-400/40 bg-amber-400/10 p-3 sm:p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="file-browser-duplicates-banner">
            <AlertCircle className="w-5 h-5 text-amber-400 flex-shrink-0" />
            <div className="flex-1 min-w-0 text-sm text-amber-100">
              <div className="font-semibold">This job has two sets of folders</div>
              <div className="text-xs text-amber-200/80 mt-0.5">
                {duplicates.map(d => `${d.legacy} → ${d.current}`).join(' · ')}. Photos filed by an earlier version sit in the old-style folders, so they don&apos;t show together. Merging moves them (nothing is deleted) and removes the empty old folders.
              </div>
            </div>
            <Button
              onClick={handleMergeLegacy}
              disabled={merging}
              size="sm"
              className="bg-amber-400 text-[#1E2530] hover:bg-amber-300 flex-shrink-0"
              data-testid="file-browser-merge-btn"
            >
              {merging ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FolderOpen className="w-4 h-4 mr-2" />}
              {merging ? 'Merging…' : 'Merge into one'}
            </Button>
          </div>
        )}
        {duplicates.length > 0 && !isAdmin && (
          <div className="mb-4 rounded-lg border border-amber-400/30 bg-amber-400/5 px-3 py-2 text-xs text-amber-200/80" data-testid="file-browser-duplicates-note">
            Some photos are in {duplicates.map(d => d.legacy).join(' and ')} as well — an admin can merge them.
          </div>
        )}
        {/* Drag & Drop Overlay for selected folder */}
        {isDragging && selectedFolder && (
          <div 
            className="absolute inset-0 bg-[#F5A623]/10 border-2 border-dashed border-[#F5A623] rounded-lg z-10 flex items-center justify-center"
            onDragOver={(e) => handleDragOver(e)}
            onDragLeave={handleDragLeave}
            onDrop={(e) => handleDrop(e, selectedFolder)}
          >
            <div className="text-center">
              <Upload className="w-12 h-12 mx-auto mb-2 text-[#F5A623]" />
              <p className="text-[#F5A623] font-medium">Drop files to upload to {selectedFolder}</p>
            </div>
          </div>
        )}

        {/* Folder Grid View */}
        {!selectedFolder && (
          <div 
            className={`grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 ${isDragging && !dragTargetFolder ? 'opacity-50' : ''}`}
            onDragOver={(e) => handleDragOver(e)}
            onDragLeave={handleDragLeave}
          >
            {subfolders.map((folder) => (
              <button
                key={folder}
                onClick={() => handleFolderClick(folder)}
                onDragOver={(e) => handleDragOver(e, folder)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, folder)}
                className={`flex items-center gap-3 p-4 bg-[#1E2530] rounded-lg hover:bg-[#252D3B] transition-all text-left group
                  ${dragTargetFolder === folder ? 'ring-2 ring-[#F5A623] bg-[#F5A623]/10 scale-105' : ''}`}
              >
                <Folder className={`w-8 h-8 flex-shrink-0 transition-colors ${dragTargetFolder === folder ? 'text-[#F5A623]' : 'text-[#F5A623]'}`} />
                <span className={`text-sm truncate transition-colors ${dragTargetFolder === folder ? 'text-[#F5A623]' : 'text-gray-300 group-hover:text-white'}`}>
                  {folder}
                </span>
              </button>
            ))}
          </div>
        )}

        {/* Files List View */}
        {selectedFolder && (
          <div
            onDragOver={(e) => handleDragOver(e)}
            onDragLeave={handleDragLeave}
            onDrop={(e) => handleDrop(e, selectedFolder)}
            className="relative"
          >
            {/* Drag overlay */}
            {isDragging && (
              <div className="absolute inset-0 bg-[#F5A623]/10 border-2 border-dashed border-[#F5A623] rounded-lg z-10 flex items-center justify-center pointer-events-none">
                <div className="text-center">
                  <Upload className="w-12 h-12 mx-auto mb-2 text-[#F5A623]" />
                  <p className="text-[#F5A623] font-medium">Drop files here</p>
                </div>
              </div>
            )}
            
            {filesLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin text-[#F5A623]" />
              </div>
            ) : files.length === 0 ? (
              <div className="text-center py-12">
                <Folder className="w-12 h-12 mx-auto mb-4 text-gray-600" />
                <p className="text-gray-400 mb-2">This folder is empty</p>
                {canUpload && (
                  <Button
                    onClick={() => {
                      setUploadFolder(selectedFolder);
                      setUploadDialogOpen(true);
                    }}
                    variant="outline"
                    className="border-white/20 text-gray-300"
                    data-testid="file-browser-empty-upload-btn"
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    Upload files
                  </Button>
                )}
              </div>
            ) : (
              <>
                {/* Sub-folders (e.g. the date folders diary photos file into) */}
                {folderItems.length > 0 && (
                  <div className={`grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2.5 ${files.some(f => f.type === 'file') ? 'mb-4' : ''}`} data-testid="file-subfolders">
                    {folderItems.map((folder) => (
                      <button
                        key={folder.id}
                        type="button"
                        onClick={() => openSubfolder(folder.name)}
                        className="flex items-center gap-2.5 p-3 bg-[#1E2530] rounded-lg hover:bg-[#252D3B] transition-colors text-left group min-w-0"
                        data-testid={`file-folder-${folder.id}`}
                      >
                        <Folder className="w-6 h-6 flex-shrink-0 text-[#F5A623]" />
                        <span className="min-w-0">
                          <span className="block text-sm text-gray-300 group-hover:text-white truncate">{folder.name}</span>
                          {typeof folder.child_count === 'number' && (
                            <span className="block text-[11px] text-gray-500">{folder.child_count} {folder.child_count === 1 ? 'item' : 'items'}</span>
                          )}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
                {viewMode === 'grid' && files.some(f => f.type === 'file') ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3" data-testid="file-grid">
                {files.filter(f => f.type === 'file').map((file) => (
                  <div key={file.id} className="group relative rounded-lg overflow-hidden bg-[#1E2530] border border-white/5 hover:border-[#F5A623]/40 transition-colors" data-testid={`file-tile-${file.id}`}>
                    <button
                      type="button"
                      onClick={() => (isPreviewable(file.name) ? handlePreview(file) : handleDownload(file))}
                      className="block w-full aspect-square bg-black/30"
                      title={file.name}
                      data-testid={`file-tile-open-${file.id}`}
                    >
                      {file.thumbnail_large_url || file.thumbnail_url ? (
                        <img src={file.thumbnail_large_url || file.thumbnail_url} alt={file.name} loading="lazy" className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-300" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">{getFileIcon(file.name)}</div>
                      )}
                    </button>
                    <div className="px-2 py-1.5">
                      <p className="text-[12px] text-white font-medium truncate" title={file.name}>{file.name}</p>
                      <p className="text-[10px] text-gray-500">{formatFileSize(file.size)}{file.modified_at ? ` · ${format(parseISO(file.modified_at), 'dd MMM yyyy')}` : ''}</p>
                      {canShare && <div className="mt-1"><ShareBadge roles={shares[file.id]} fileId={file.id} /></div>}
                    </div>
                    <div className="absolute top-1.5 right-1.5 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      {canShare && (
                        <FileShareControl api={api} projectId={entityId} files={[file]} folder={selectedFolder} roles={shares[file.id]} onSaved={onShareSaved} compact testId={`file-share-${file.id}`} />
                      )}
                      <button type="button" onClick={() => handleDownload(file)} className="p-1.5 rounded-md bg-black/60 text-gray-200 hover:text-white" title="Download"><Download className="w-3.5 h-3.5" /></button>
                      {canDelete && isAdmin && (
                        <button type="button" onClick={() => handleDelete(file)} className="p-1.5 rounded-md bg-black/60 text-red-300 hover:text-red-200" title="Delete"><Trash2 className="w-3.5 h-3.5" /></button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : files.some(f => f.type === 'file') ? (
              <div className="space-y-2" data-testid="file-list">
                {files.filter(f => f.type === 'file').map((file) => (
                  <div
                    key={file.id}
                    className="flex items-center justify-between p-3 bg-[#1E2530] rounded-lg hover:bg-[#252D3B] transition-colors group"
                    data-testid={`file-row-${file.id}`}
                  >
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      {file.thumbnail_url ? (
                        <button type="button" onClick={() => handlePreview(file)} className="w-12 h-12 rounded-md overflow-hidden flex-shrink-0 bg-black/30 border border-white/10" title="Preview" data-testid={`file-row-thumb-${file.id}`}>
                          <img src={file.thumbnail_url} alt="" loading="lazy" className="w-full h-full object-cover" />
                        </button>
                      ) : getFileIcon(file.name)}
                      <div className="flex-1 min-w-0">
                        <p className="text-white text-sm font-medium truncate">{file.name}</p>
                        <div className="flex items-center gap-3 text-xs text-gray-500">
                          <span>{formatFileSize(file.size)}</span>
                          {file.modified_at && (
                            <span>{format(parseISO(file.modified_at), 'dd MMM yyyy')}</span>
                          )}
                          {canShare && <ShareBadge roles={shares[file.id]} fileId={file.id} />}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      {canShare && (
                        <FileShareControl api={api} projectId={entityId} files={[file]} folder={selectedFolder} roles={shares[file.id]} onSaved={onShareSaved} testId={`file-share-${file.id}`} />
                      )}
                      {isPreviewable(file.name) && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handlePreview(file)}
                          className="text-gray-400 hover:text-white hover:bg-white/10"
                          title="Preview"
                          data-testid={`file-preview-${file.id}`}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDownload(file)}
                        className="text-gray-400 hover:text-white hover:bg-white/10"
                        title="Download"
                        data-testid={`file-download-${file.id}`}
                      >
                        <Download className="w-4 h-4" />
                      </Button>
                      {file.web_url && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleOpenInSharePoint(file)}
                          className="text-gray-400 hover:text-white hover:bg-white/10"
                          title="Open in SharePoint"
                        >
                          <ExternalLink className="w-4 h-4" />
                        </Button>
                      )}
                      {canDelete && isAdmin && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDelete(file)}
                          className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : null}
              </>
            )}
          </div>
        )}
      </CardContent>

      {/* Upload Dialog */}
      <Dialog open={uploadDialogOpen} onOpenChange={(o) => { if (!uploading) { setUploadDialogOpen(o); if (!o) setSelectedFiles([]); } }}>
        <DialogContent className="bg-[#2A3240] border-white/10 max-w-md" data-testid="file-upload-dialog">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Upload className="w-5 h-5 text-[#F5A623]" />
              Upload files
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              Pick one or many files for the selected folder — big drawings and photos (up to 250MB) are sent in chunks
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div className="space-y-2">
              <label className="text-sm text-gray-300">Destination Folder</label>
              <Select value={uploadFolder} onValueChange={setUploadFolder} disabled={uploading}>
                <SelectTrigger className="bg-[#1E2530] border-white/20 text-white" data-testid="file-upload-folder-select">
                  <SelectValue placeholder="Select folder" />
                </SelectTrigger>
                <SelectContent className="bg-[#2A3240] border-white/10">
                  {(selectedFolder && !subfolders.includes(selectedFolder) ? [selectedFolder, ...subfolders] : subfolders).map((folder) => (
                    <SelectItem key={folder} value={folder} className="text-white focus:bg-[#F5A623]/20">
                      {folder}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <label className="text-sm text-gray-300">Files {selectedFiles.length > 0 && <span className="text-gray-500">· {selectedFiles.length} selected · {formatFileSize(selectedFiles.reduce((a, f) => a + f.size, 0))}</span>}</label>
              <input
                type="file"
                multiple
                onChange={(e) => { addSelectedFiles(e.target.files); e.target.value = ''; }}
                className="hidden"
                id="file-upload"
                disabled={uploading}
                data-testid="file-upload-input"
              />
              {selectedFiles.length > 0 && (
                <ul className="max-h-48 overflow-y-auto divide-y divide-white/5 rounded-lg border border-white/10 bg-[#1E2530]" data-testid="file-upload-list">
                  {selectedFiles.map((f, i) => (
                    <li key={`${f.name}-${f.size}`} className="flex items-center gap-2 px-3 py-2" data-testid={`file-upload-item-${i}`}>
                      {getFileIcon(f.name)}
                      <span className="text-sm text-white truncate flex-1">{f.name}</span>
                      <span className="text-xs text-gray-500 flex-shrink-0">{formatFileSize(f.size)}{f.size > MAX_UPLOAD_BYTES ? ' · too big' : f.size > SIMPLE_UPLOAD_BYTES ? ' · chunked' : ''}</span>
                      {!uploading && (
                        <button type="button" onClick={() => setSelectedFiles(prev => prev.filter((_, j) => j !== i))} className="text-gray-500 hover:text-red-400" data-testid={`file-upload-remove-${i}`}>
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              )}
              <label
                htmlFor="file-upload"
                className={`flex items-center justify-center gap-2 border-2 border-dashed border-white/20 rounded-lg cursor-pointer hover:border-[#F5A623]/50 transition-colors ${selectedFiles.length ? 'p-3' : 'p-6'} ${uploading ? 'pointer-events-none opacity-50' : ''}`}
                data-testid="file-upload-picker"
              >
                {selectedFiles.length ? (
                  <span className="text-sm text-[#F5A623] inline-flex items-center gap-1.5"><Upload className="w-4 h-4" /> Add more files</span>
                ) : (
                  <div className="text-center">
                    <Upload className="w-8 h-8 mx-auto mb-2 text-gray-500" />
                    <p className="text-gray-400 text-sm">Tap to select files — you can pick several at once</p>
                  </div>
                )}
              </label>
            </div>

            {uploadProgress && (
              <div className="space-y-1.5" data-testid="file-upload-progress">
                <div className="flex justify-between text-xs text-gray-400">
                  <span className="truncate mr-3">Uploading {uploadProgress.done + 1} of {uploadProgress.total} — {uploadProgress.current}{uploadProgress.fraction > 0 && uploadProgress.fraction < 1 ? ` (${Math.round(uploadProgress.fraction * 100)}%)` : ''}</span>
                  <span>{Math.round(((uploadProgress.done + (uploadProgress.fraction || 0)) / uploadProgress.total) * 100)}%</span>
                </div>
                <div className="h-1.5 rounded-full bg-white/10 overflow-hidden">
                  <div className="h-full bg-[#F5A623] transition-[width] duration-300" style={{ width: `${((uploadProgress.done + (uploadProgress.fraction || 0)) / uploadProgress.total) * 100}%` }} />
                </div>
              </div>
            )}

            <div className="flex gap-3 pt-4">
              <Button
                variant="outline"
                onClick={() => {
                  setUploadDialogOpen(false);
                  setSelectedFiles([]);
                }}
                disabled={uploading}
                className="flex-1 border-white/20 text-gray-300"
                data-testid="file-upload-cancel-btn"
              >
                Cancel
              </Button>
              <Button
                onClick={handleUpload}
                disabled={uploading || !selectedFiles.length || !uploadFolder}
                className="flex-1 bg-[#F5A623] text-[#1E2530] hover:bg-[#D98E1D]"
                data-testid="file-upload-submit-btn"
              >
                {uploading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    {selectedFiles.length > 1 ? `Upload ${selectedFiles.length} files` : 'Upload'}
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Lightbox — browse the folder's images / PDFs */}
      {lightboxIndex !== null && previewFiles[lightboxIndex] && (
        <FileLightbox
          entityType={entityType}
          entityId={entityId}
          files={previewFiles}
          index={lightboxIndex}
          onIndexChange={setLightboxIndex}
          onClose={() => setLightboxIndex(null)}
          onDownload={handleDownload}
        />
      )}
    </Card>
  );
}

export default FileBrowser;
